import React, { useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
  DrawerBody,
} from "components/Graphs/Drawer";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { Textarea } from "components/Graphs/TextArea";
import { RadioGroup, RadioGroupItem } from "components/Graphs/RadioGroup";
import {
  ChevronRight,
  ChevronDown,
  PlusCircle,
  Trash2,
  X,
  Tag,
} from "lucide-react";
import type {
  CloudPolicy,
  CloudProvider,
  CreateCloudPolicyRequest,
  CreateCloudQuota,
} from "components/CloudPolicy/types";
import { Badge } from "components/Graphs/Badge";
import { FlatCard } from "components/UI-lib";
import {
  TagIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";

interface CreatePolicySheetProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onCreatePolicy: (policy: CreateCloudPolicyRequest) => void;
  organizationId: string;
  cloudProviders: CloudProvider[];
  errorCreatePolicy: string | null;
  setErrorCreatePolicy: (error: string | null) => void;
}

interface QuotaWithType extends CreateCloudQuota {
  quotaType?: "instance" | "gpu";
}

export const DEFAULT_RULE_OPTIONS = [
  { value: "DEFAULT_RULE_ALLOW", label: "Allow" },
  { value: "DEFAULT_RULE_DENY", label: "Deny" },
];

export default function CreatePolicy({
  isOpen,
  onOpenChange,
  onCreatePolicy,
  organizationId,
  cloudProviders,
  errorCreatePolicy,
  setErrorCreatePolicy,
}: CreatePolicySheetProps) {
  const [policyName, setPolicyName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [currentTag, setCurrentTag] = useState("");
  const [cloudCredId, setCloudCredId] = useState<string>("");
  const [defaultRule, setDefaultRule] = useState<
    "DEFAULT_RULE_ALLOW" | "DEFAULT_RULE_DENY"
  >("DEFAULT_RULE_ALLOW");
  const [quotas, setQuotas] = useState<QuotaWithType[]>([]);
  const [showMoreContext, setShowMoreContext] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddTag = () => {
    if (currentTag && !tags.includes(currentTag)) {
      setTags([...tags, currentTag]);
      setCurrentTag("");
    }
  };
  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleAddQuota = () => {
    setQuotas([...quotas, { quotaType: "instance", limit: 1 }]);
  };

  const handleQuotaChange = (
    index: number,
    field: keyof QuotaWithType,
    value: string | number
  ) => {
    const newQuotas = [...quotas];
    if (field === "limit") {
      // Allow empty string, convert to number only if not empty
      newQuotas[index][field] = value === "" ? ("" as any) : Number(value) || 0;
    } else if (field === "quotaType") {
      newQuotas[index][field] = value as "instance" | "gpu";
      // Clear the other type when switching
      if (value === "instance") {
        newQuotas[index].gpu_type = "";
      } else {
        newQuotas[index].instance_type = "";
      }
    } else {
      newQuotas[index][field] = value as string;
    }
    setQuotas(newQuotas);
  };

  const handleRemoveQuota = (index: number) => {
    setQuotas(quotas.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    setError(null);
    setErrorCreatePolicy(null);

    // Basic required field validation
    if (!policyName || !cloudCredId) {
      setError("Policy Name and Cloud Provider are required.");
      return;
    }

    // Quota validation
    if (quotas.length > 0) {
      for (let i = 0; i < quotas.length; i++) {
        const quota = quotas[i];

        // Check if limit is empty, zero, or invalid
        const limitValue = String(quota.limit).trim();
        if (
          limitValue === "" ||
          Number(limitValue) < 0 ||
          isNaN(Number(limitValue))
        ) {
          setError(`Quota ${i + 1}: Limit must be a positive number.`);
          return;
        }

        // Check if resource type is selected and specified
        if (!quota.quotaType) {
          setError(`Quota ${i + 1}: Please select Instance Type or GPU Type.`);
          return;
        }

        if (quota.quotaType === "instance" && !quota.instance_type) {
          setError(`Quota ${i + 1}: Instance Type is required.`);
          return;
        }

        if (quota.quotaType === "gpu" && !quota.gpu_type) {
          setError(`Quota ${i + 1}: GPU Type is required.`);
          return;
        }
      }
    }

    const newPolicy: CreateCloudPolicyRequest = {
      organization_id: organizationId,
      cloud_cred_id: cloudCredId,
      default_rule: defaultRule,
      context: {
        name: policyName,
        description: description,
        tags: tags,
      },
      quotas: quotas.map((q) => ({
        instance_type: q.quotaType === "instance" ? q.instance_type : "",
        gpu_type: q.quotaType === "gpu" ? q.gpu_type : "",
        limit: Number(q.limit),
        unit: "units",
        current: 0,
      })),
    };
    onCreatePolicy(newPolicy);
    resetForm();
    onOpenChange(false);
  };

  const resetForm = () => {
    setPolicyName("");
    setDescription("");
    setTags([]);
    setCurrentTag("");
    setCloudCredId("");
    setDefaultRule("DEFAULT_RULE_ALLOW");
    setShowMoreContext(false);
    setQuotas([]);
    setError(null);
    setErrorCreatePolicy(null);
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) resetForm();
        onOpenChange(open);
      }}
    >
      <DrawerContent className="sm:max-w-lg w-[90vw] overflow-y-auto">
        <DrawerHeader>
          <DrawerTitle>Create New Cloud Policy</DrawerTitle>
          <div className="text-sm text-secondary">
            Define a new policy to manage a cloud provider's resource access and
            quotas for an organization.
          </div>
        </DrawerHeader>
        <DrawerBody>
          <div className="grid pt-3 pb-3">
            <div className="grid gap-2">
              <label htmlFor="policyName" className="text-white text-sm">
                Policy Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="policyName"
                value={policyName}
                onChange={(e) => setPolicyName(e.target.value)}
                placeholder="e.g., Standard AWS Policy"
              />
            </div>
            {/* Collapsible More Context Section */}
            {/* Collapsible More Context Section */}
            <div className="grid gap-2 mt-2">
              <button
                type="button"
                onClick={() => setShowMoreContext(!showMoreContext)}
                className="flex items-center text-gray-400 text-xs hover:text-white transition-colors w-fit"
              >
                Add more context
                {showMoreContext ? (
                  <ChevronDown className="h-4 w-4 ml-1" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-1" />
                )}
              </button>

              <AnimatePresence initial={false}>
                {showMoreContext && (
                  <motion.div
                    key="content"
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={{
                      open: { opacity: 1, height: "auto" },
                      collapsed: { opacity: 0, height: 0 },
                    }}
                    transition={{
                      duration: 0.4,
                      ease: [0.04, 0.62, 0.23, 0.98],
                    }}
                    className="overflow-hidden"
                  >
                    <div className="grid gap-4 pt-2">
                      <div className="grid gap-2">
                        <label
                          htmlFor="description"
                          className="text-sm font-medium text-gray-300"
                        >
                          Description
                        </label>
                        <Textarea
                          id="description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Briefly describe this policy's purpose"
                        />
                      </div>
                      <div className="grid gap-2">
                        <label
                          htmlFor="tags"
                          className="text-sm font-medium text-gray-300"
                        >
                          Tags
                        </label>
                        <div className="flex gap-2">
                          <Input
                            id="tags"
                            value={currentTag}
                            onChange={(e) => setCurrentTag(e.target.value)}
                            placeholder="Add a tag"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddTag();
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={handleAddTag}
                          >
                            <Tag className="h-4 w-4 mr-2" />
                            Add Tag
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {tags.map((tag) => (
                            <Badge key={tag} variant="neutral">
                              {tag}
                              <button
                                type="button"
                                onClick={() => handleRemoveTag(tag)}
                                className="ml-1.5 -mr-0.5 rounded-full hover:bg-white/20 p-0.5 transition-colors"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="grid gap-2 mt-6">
              <label htmlFor="cloudCredId" className="text-white text-sm">
                Cloud Provider <span className="text-red-500">*</span>
              </label>
              <Select
                value={cloudCredId}
                onValueChange={(value) => setCloudCredId(value)}
              >
                <SelectTrigger id="cloudCredId">
                  <SelectValue placeholder="Select a cloud provider" />
                </SelectTrigger>
                <SelectContent>
                  {cloudProviders.map((cred) => (
                    <SelectItem key={cred.id} value={cred.id}>
                      {cred.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2 mt-6">
              <label htmlFor="defaultRule" className="text-white text-sm">
                Default Rule
              </label>
              <Select
                value={defaultRule}
                onValueChange={(value) => setDefaultRule(value as any)}
              >
                <SelectTrigger id="defaultRule">
                  <SelectValue placeholder="Select default rule" />
                </SelectTrigger>
                <SelectContent>
                  {DEFAULT_RULE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4 mt-6">
              <h4 className="text-md font-medium text-white">Quotas</h4>
              {quotas.map((quota, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 gap-4 p-4 border border-zinc-800 rounded-md relative bg-secondaryBg p-4"
                >
                  <Button
                    variant="ghost"
                    className="absolute top-2 right-2"
                    onClick={() => handleRemoveQuota(index)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                  <div className="flex flex-col items-start gap-4">
                    <div className="w-full">
                      <label className="text-white text-sm font-semibold mb-2 block">
                        Select a Resource Type
                      </label>
                      <RadioGroup
                        value={quota.quotaType}
                        onValueChange={(value) =>
                          handleQuotaChange(index, "quotaType", value)
                        }
                        className="flex flex-row gap-x-3"
                      >
                        <div className="flex items-center gap-x-3">
                          <RadioGroupItem
                            value="instance"
                            id={`instance-${index}`}
                          />
                          <label
                            htmlFor={`instance-${index}`}
                            className="text-sm text-white"
                          >
                            Instance Type
                          </label>
                        </div>
                        <div className="flex items-center gap-x-3">
                          <RadioGroupItem value="gpu" id={`gpu-${index}`} />
                          <label
                            htmlFor={`gpu-${index}`}
                            className="text-sm text-white"
                          >
                            GPU Type
                          </label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="gap-2 w-full">
                      <label
                        htmlFor={`quota-type-${index}`}
                        className="text-white text-sm"
                      >
                        {quota.quotaType === "instance"
                          ? "Instance Type"
                          : "GPU Type"}
                      </label>
                      <Input
                        id={`quota-type-${index}`}
                        value={
                          quota.quotaType === "instance"
                            ? quota.instance_type
                            : quota.gpu_type
                        }
                        onChange={(e) =>
                          handleQuotaChange(
                            index,
                            quota.quotaType === "instance"
                              ? "instance_type"
                              : "gpu_type",
                            e.target.value
                          )
                        }
                        placeholder={
                          quota.quotaType === "instance"
                            ? "e.g., m5.large, n1-standard-1"
                            : "e.g., T4, V100, A100"
                        }
                      />
                    </div>

                    <div className="gap-2 w-full">
                      <label
                        htmlFor={`quota-limit-${index}`}
                        className="text-white text-sm"
                      >
                        Limit
                      </label>
                      <Input
                        id={`quota-limit-${index}`}
                        type="number"
                        value={quota.limit}
                        onChange={(e) =>
                          handleQuotaChange(index, "limit", e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const limitValue = String(quota.limit || "").trim();
                            if (
                              !limitValue ||
                              Number(limitValue) < 0 ||
                              isNaN(Number(limitValue))
                            ) {
                              setError(
                                `Quota ${
                                  index + 1
                                }: Limit must be a positive number.`
                              );
                            } else {
                              setError(null);
                            }
                          }
                        }}
                        placeholder="e.g., 10"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddQuota}
                className="w-full"
              >
                <PlusCircle className="mr-2 h-4 w-4" /> Add Quota
              </Button>
            </div>
          </div>
        </DrawerBody>
        <DrawerFooter className="mt-auto pt-6">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row justify-end gap-2">
              <DrawerClose asChild>
                <Button variant="secondary">Cancel</Button>
              </DrawerClose>
              <Button onClick={handleSubmit}>Create Policy</Button>
            </div>
            {errorCreatePolicy && (
              <div className="text-red-500 text-xs">{errorCreatePolicy}</div>
            )}
            {error && <div className="text-red-500 text-xs">{error}</div>}
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
